import React from 'react';
import { Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const CATEGORIES = [
  ['hotels', 'Hotels'], ['tours', 'Tours'], ['activities', 'Activities'],
  ['visa', 'Visa'], ['airfares', 'Airfares'], ['transfers', 'Transfers'], ['other', 'Other'],
];

const CURRENCIES = ['INR', 'EUR', 'GBP', 'USD', 'SGD', 'AED'];

const REVENUE_METHODS = [
  { value: '', label: 'Manual margin (legacy)' },
  { value: 'net_rate_markup', label: 'Net Rate + Markup' },
  { value: 'commission', label: 'Commission' },
  { value: 'fixed_commission', label: 'Fixed Commission' },
  { value: 'commission_markup', label: 'Commission + Markup' },
  { value: 'fixed_fee', label: 'Fixed Fee' },
  { value: 'no_revenue', label: 'No Revenue' },
  { value: 'custom', label: 'Custom (manual margin)' },
];

const fmt = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export default function CostBreakdownRow({ item, idx, vendors = [], onUpdate, onRemove }) {
  const method = item.revenueMethod || '';

  // Copy the vendor's DEFAULT commercial terms onto this item at selection
  // time — editable afterwards, and never retroactively affected by later
  // vendor-default changes (item keeps its own copy).
  const handleVendorChange = (vendorId) => {
    const patch = { vendorId };
    const v = vendors.find((x) => x.vendorId === vendorId);
    if (v && v.commercialTermsConfigured) {
      patch.revenueMethod = v.revenueMethod || '';
      patch.commissionPct = v.defaultCommissionPct || 0;
      patch.fixedAmount = v.defaultFixedAmount || 0;
      patch.markupType = v.defaultMarkupType || 'percent';
      patch.markupValue = v.defaultMarkupValue || 0;
    }
    onUpdate(idx, patch);
  };

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/50">
      <td className="px-2 py-2">
        <select
          value={item.category || 'other'}
          onChange={(e) => onUpdate(idx, { category: e.target.value })}
          className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
          data-testid={'cost-cat-' + idx}
        >
          {CATEGORIES.map((c) => <option key={c[0]} value={c[0]}>{c[1]}</option>)}
        </select>
      </td>
      <td className="px-2 py-2">
        <Input
          value={item.description || ''}
          onChange={(e) => onUpdate(idx, { description: e.target.value })}
          className="h-9 rounded-md text-sm"
          placeholder="e.g., 4N at Hotel Le Grand"
          data-testid={'cost-desc-' + idx}
        />
        <Input
          value={item.supplierRef || ''}
          onChange={(e) => onUpdate(idx, { supplierRef: e.target.value })}
          className="h-7 rounded-md text-xs mt-1 bg-slate-50"
          placeholder="Supplier ref, e.g., Booking 9182734 / Wakeup Goa"
          data-testid={'cost-supplier-' + idx}
        />
      </td>
      <td className="px-2 py-2">
        <select
          value={item.vendorId || ''}
          onChange={(e) => handleVendorChange(e.target.value)}
          className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
          data-testid={'cost-vendor-' + idx}
        >
          <option value="">No vendor</option>
          {vendors.map((v) => <option key={v.vendorId} value={v.vendorId}>{v.name}</option>)}
        </select>
      </td>
      <td className="px-2 py-2">
        <Input
          type="number"
          value={item.cost || 0}
          onChange={(e) => onUpdate(idx, { cost: parseFloat(e.target.value) || 0 })}
          className="h-9 rounded-md text-sm text-right"
          data-testid={'cost-val-' + idx}
        />
      </td>
      <td className="px-2 py-2">
        <select
          value={item.currency || 'INR'}
          onChange={(e) => onUpdate(idx, { currency: e.target.value })}
          className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
          data-testid={'cost-cur-' + idx}
        >
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td className="px-2 py-2 text-right text-slate-700 font-mono text-xs">
        {fmt(item.costInr)}
        {item.currency !== 'INR' && (
          <div className="text-[10px] text-slate-400 font-normal">
            {'@ \u20B9' + item.exchangeRate + '/' + item.currency}
            {item.rateDate ? <div>{'rate frozen ' + item.rateDate}</div> : null}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        <select
          value={method}
          onChange={(e) => onUpdate(idx, { revenueMethod: e.target.value })}
          className="w-full h-9 rounded-md border border-slate-200 px-2 text-xs bg-white"
          data-testid={'cost-revenue-method-' + idx}
        >
          {REVENUE_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </td>
      <td className="px-2 py-2">
        {(!method || method === 'custom') && (
          <Input
            type="number"
            value={item.margin || 0}
            onChange={(e) => onUpdate(idx, { margin: parseFloat(e.target.value) || 0 })}
            className="h-9 rounded-md text-sm text-right"
            placeholder="Margin"
            data-testid={'cost-margin-' + idx}
          />
        )}
        {method === 'commission' && (
          <Input
            type="number"
            value={item.commissionPct || 0}
            onChange={(e) => onUpdate(idx, { commissionPct: parseFloat(e.target.value) || 0 })}
            className="h-9 rounded-md text-sm text-right"
            placeholder="Commission %"
            data-testid={'cost-commission-pct-' + idx}
          />
        )}
        {method === 'fixed_commission' && (
          <Input
            type="number"
            value={item.fixedAmount || 0}
            onChange={(e) => onUpdate(idx, { fixedAmount: parseFloat(e.target.value) || 0 })}
            className="h-9 rounded-md text-sm text-right"
            placeholder="Fixed \u20B9"
            data-testid={'cost-fixed-amount-' + idx}
          />
        )}
        {method === 'fixed_fee' && (
          <Input
            type="number"
            value={item.fixedAmount || 0}
            onChange={(e) => onUpdate(idx, { fixedAmount: parseFloat(e.target.value) || 0 })}
            className="h-9 rounded-md text-sm text-right"
            placeholder="Fee \u20B9"
            data-testid={'cost-fixed-amount-' + idx}
          />
        )}
        {method === 'net_rate_markup' && (
          <div className="flex gap-1">
            <Input
              type="number"
              value={item.markupValue || 0}
              onChange={(e) => onUpdate(idx, { markupValue: parseFloat(e.target.value) || 0 })}
              className="h-9 rounded-md text-sm text-right"
              placeholder="Markup"
              data-testid={'cost-markup-value-' + idx}
            />
            <select
              value={item.markupType || 'percent'}
              onChange={(e) => onUpdate(idx, { markupType: e.target.value })}
              className="h-9 rounded-md border border-slate-200 px-1 text-xs bg-white"
              data-testid={'cost-markup-type-' + idx}
            >
              <option value="percent">%</option>
              <option value="fixed">{'\u20B9'}</option>
            </select>
          </div>
        )}
        {method === 'commission_markup' && (
          <div className="space-y-1">
            <Input
              type="number"
              value={item.commissionPct || 0}
              onChange={(e) => onUpdate(idx, { commissionPct: parseFloat(e.target.value) || 0 })}
              className="h-7 rounded-md text-xs text-right"
              placeholder="Commission %"
              data-testid={'cost-commission-pct-' + idx}
            />
            <div className="flex gap-1">
              <Input
                type="number"
                value={item.markupValue || 0}
                onChange={(e) => onUpdate(idx, { markupValue: parseFloat(e.target.value) || 0 })}
                className="h-7 rounded-md text-xs text-right"
                placeholder="Markup"
                data-testid={'cost-markup-value-' + idx}
              />
              <select
                value={item.markupType || 'percent'}
                onChange={(e) => onUpdate(idx, { markupType: e.target.value })}
                className="h-7 rounded-md border border-slate-200 px-1 text-xs bg-white"
                data-testid={'cost-markup-type-' + idx}
              >
                <option value="percent">%</option>
                <option value="fixed">{'\u20B9'}</option>
              </select>
            </div>
          </div>
        )}
        {method === 'no_revenue' && (
          <span className="text-xs text-slate-400 italic">No revenue</span>
        )}
      </td>
      <td className="px-2 py-2">
        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1 cursor-pointer">
          <input
            type="checkbox"
            checked={!!item.isProvisional}
            onChange={(e) => onUpdate(idx, { isProvisional: e.target.checked })}
            data-testid={'cost-provisional-' + idx}
          />
          Provisional rate
        </label>
        <Input
          value={item.bookingRef || ''}
          onChange={(e) => onUpdate(idx, { bookingRef: e.target.value })}
          className="h-7 rounded-md text-xs"
          placeholder="Booking ref / PNR"
          data-testid={'cost-booking-ref-' + idx}
        />
        <select
          value={item.bookingStatus || 'confirmed'}
          onChange={(e) => onUpdate(idx, { bookingStatus: e.target.value })}
          className="w-full h-7 rounded-md border border-slate-200 px-1 text-xs bg-white mt-1"
          data-testid={'cost-booking-status-' + idx}
        >
          <option value="booked">Booked</option>
          <option value="confirmed">Confirmed</option>
          <option value="amended">Amended</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </td>
      <td className="px-2 py-2 text-right font-semibold text-teal-700">
        {fmt(item.finalPrice)}
      </td>
      <td className="px-2 py-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600"
          onClick={onRemove}
          data-testid={'cost-remove-' + idx}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  );
}

export { CATEGORIES, CURRENCIES, fmt };
